import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AsyncSelect from 'react-select/async';
import Layout from '../Layout';
import { useLocation } from 'react-router-dom';
import {
  createPackage,
  generateWebsiteV2JourneyDraft,
  updatePackage,
} from '../../api/Packages';
import { loadAirportLocations, loadCityLocations } from '../../api/Location';
import { estimateActivities, type ActivityEstimate } from '../../api/Activities';
import ActivityTourOptionPicker, { type ActivityOptionSelection } from './ActivityTourOptionPicker';
import { usePermissions, useRole } from '../../hooks/usePermissions';
import usePageExitConfirmation from '../../hooks/usePageExitConfirmation';
import {
  getCabMaxOccupancy,
  normalizePackageCabPricing,
  normalizePackageFaq,
  normalizePackagePlaces,
  normalizePackageRouteLegs,
  normalizeStringArray,
  PACKAGE_CAB_TYPES,
} from '../../utils/packageTemplate';
import {
  PackageAiGeneratedField,
  PackageAiRequestMeta,
  PackageCabPricingRow,
  PackageCabType,
  PackageRouteLeg,
  TravelPackage,
} from '../../types/TravelPackage';
import { LocationOption } from '../../types/Location';
import { Hotel, TravellerInfo } from '../../types/Quotation';
import { getDatePlusDays } from '../../utils/date';
import HotelListModal from '../QuotationBuilder/HotelListModal';
import FeaturedHotelsSection from './FeaturedHotelsSection';
import {
  isPackageBuilderLocationState,
  PackageBuilderLocationState,
} from './contracts';
import {
  normalizeItineraryDraftDay,
  type GuidedItineraryDay,
} from './guidedItinerary';
import '../../css/package-builder/sections.css';
import '../../css/package-builder/guided.css';

type BuilderPlace = {
  cityName: string;
  cityCode: string;
  nights: number;
};

type SelectedHotelRecord = {
  placeIndex: number;
  hotel: Hotel;
};

type BuilderItineraryDay = GuidedItineraryDay;

type RouteLegDefinition = PackageRouteLeg & {
  key: string;
  fromLabel: string;
  toLabel: string;
};

export const formatAirportOptionLabel = (option: LocationOption): string => {
  const city = option.label.trim();
  const airportCode = option.value.trim();

  if (!city || !airportCode || city.toLowerCase() === airportCode.toLowerCase()) {
    return city || airportCode;
  }

  return `${city} - ${airportCode}`;
};

export const buildRouteLegDefinitions = (
  places: BuilderPlace[],
  arrivalAt: string,
  departureFrom: string,
  current: PackageRouteLeg[] = []
): RouteLegDefinition[] => {
  const definitions: RouteLegDefinition[] = [];
  const firstCity = places[0]?.cityName || 'First city';
  const lastCity = places[places.length - 1]?.cityName || 'Last city';
  const findExisting = (fromStopIndex: number, toStopIndex: number, fromEndpoint?: 'arrival_airport', toEndpoint?: 'departure_airport') =>
    current.find((leg) => leg.fromStopIndex === fromStopIndex && leg.toStopIndex === toStopIndex
      && leg.fromEndpoint === fromEndpoint && leg.toEndpoint === toEndpoint) || {};

  if (arrivalAt.trim() && places.length > 0) {
    definitions.push({
      ...findExisting(-1, 0, 'arrival_airport'),
      key: 'arrival-airport',
      fromStopIndex: -1,
      toStopIndex: 0,
      fromEndpoint: 'arrival_airport',
      fromLabel: arrivalAt.trim(),
      toLabel: firstCity,
    });
  }
  places.slice(0, -1).forEach((place, index) => {
    definitions.push({
      ...findExisting(index, index + 1),
      key: `city-${index}`,
      fromStopIndex: index,
      toStopIndex: index + 1,
      fromLabel: place.cityName || `City ${index + 1}`,
      toLabel: places[index + 1]?.cityName || `City ${index + 2}`,
    });
  });
  if (departureFrom.trim() && places.length > 0) {
    definitions.push({
      ...findExisting(places.length - 1, places.length, undefined, 'departure_airport'),
      key: 'departure-airport',
      fromStopIndex: places.length - 1,
      toStopIndex: places.length,
      toEndpoint: 'departure_airport',
      fromLabel: lastCity,
      toLabel: departureFrom.trim(),
    });
  }
  return definitions;
};

type BuilderFaq = {
  question: string;
  answer: string;
};

type BuilderActivityPricingRow = {
  id: string;
  imageKey: string;
  name: string;
  day: number;
  description: string;
  adultPrice: number | '';
  childPrice: number | '';
  imageFile: File | null;
  imagePreview: string;
  activityRef?: string;
  provider?: string;
  pricingMode?: ActivityEstimate['mode'];
  ticketPricingAvailable?: boolean;
  activityCabPrices?: NonNullable<ActivityEstimate['cabOptions']>;
  supplierSelection?: Record<string, unknown>;
  inclusionType: 'mandatory' | 'optional';
};

type ReviewTagTone = 'default' | 'gold' | 'blue' | 'green' | 'red';

const AI_MANAGED_FIELDS: PackageAiGeneratedField[] = [
  'title',
  'summary',
  'durationLabel',
  'routeLabel',
  'destinationSlugs',
  'primaryDestinationSlug',
  'styleTags',
  'bestFor',
  'highlights',
  'itinerary',
  'includedExperiences',
  'stayStyle',
  'seo',
];

const PACKAGE_INCLUSIONS = [
  'Hotel',
  'Selected Meal',
  'Sightseeing & Activities',
  'Transfers',
  'Prime Guidance',
];

const PACKAGE_EXCLUSIONS = [
  'Entrance fees and activity charges excluded.',
  'Unspecified meals and refreshments are excluded.',
  'Personal expenses tips and porterage excluded.',
  'Early check-in and late checkout excluded.',
  'Seat selection and excess baggage excluded.',
  'Anything not listed under inclusions excluded.',
];

const EMPTY_PLACE: BuilderPlace = {
  cityName: '',
  cityCode: '',
  nights: 1,
};

const DEFAULT_HOTEL_TRAVELLERS: TravellerInfo = {
  adults: 2,
  children: 0,
  infants: 0,
};

const BASE_PACKAGE_PASSENGERS = DEFAULT_HOTEL_TRAVELLERS.adults;
export const TRANSFER_NOTE_MAX_WORDS = 50;

export const countWords = (value: string | null | undefined) => {
  const words = String(value || '').trim().match(/\S+/g);
  return words?.length || 0;
};

export const limitToWordCount = (value: string, maxWords = TRANSFER_NOTE_MAX_WORDS) => {
  if (countWords(value) <= maxWords) return value;
  return (value.match(/\S+/g) || []).slice(0, maxWords).join(' ');
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const cleanActivityDescription = (value: string | null | undefined) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
export const summarizeRaynaActivityDescription = (value: string | null | undefined, maxLength = 220) => {
  const text = cleanActivityDescription(value);
  if (text.length <= maxLength) return text;
  const sentenceEnd = text.lastIndexOf('.', maxLength - 1);
  if (sentenceEnd >= Math.floor(maxLength * 0.45)) return text.slice(0, sentenceEnd + 1);
  const wordEnd = text.lastIndexOf(' ', maxLength - 1);
  return `${text.slice(0, wordEnd > 0 ? wordEnd : maxLength - 1).trim()}…`;
};

type ActivityConfigInput = {
  id: string;
  name: string;
  day: number;
  description: string;
  picture_url?: string;
  activityRef?: string;
  provider?: string;
  supplierSelection?: Record<string, unknown>;
  inclusionType: 'mandatory' | 'optional';
};

export const buildActivityConfig = (activities: ActivityConfigInput[]) => {
  const byDay = new Map<number, ActivityConfigInput[]>();
  activities.forEach((activity) => {
    const day = Math.max(1, Number(activity.day || 1));
    byDay.set(day, [...(byDay.get(day) || []), activity]);
  });
  const dayEntries: Array<[number, ActivityConfigInput[]]> = [];
  byDay.forEach((items, day) => dayEntries.push([day, items]));
  return {
    version: 1,
    days: dayEntries.sort(([left], [right]) => left - right).map(([day, items]) => {
      const snapshot = (activity: ActivityConfigInput) => ({
        id: activity.id,
        name: activity.name,
        source: activity.activityRef ? 'catalog' : 'manual',
        ...(activity.activityRef ? { activityRef: activity.activityRef, provider: activity.provider || 'RAYNA' } : {}),
        ...(activity.supplierSelection ? { supplierSelection: activity.supplierSelection } : {}),
        ...(activity.description ? { description: activity.activityRef ? summarizeRaynaActivityDescription(activity.description) : activity.description } : {}),
        ...(activity.picture_url ? { image: activity.picture_url } : {}),
      });
      const optional = items.filter((activity) => activity.inclusionType === 'optional');
      return {
        day,
        required: items.filter((activity) => activity.inclusionType === 'mandatory').map(snapshot),
        optionalGroups: optional.length ? [{
          id: `day-${day}-optional-experiences`,
          title: 'Optional experiences',
          minSelections: 0,
          maxSelections: optional.length,
          options: optional.map(snapshot),
        }] : [],
      };
    }),
  };
};
const LEGACY_MANDATORY_ACTIVITY_POINT_PREFIX = 'Signature experience: ';
const mandatoryActivityPointTemplates = [
  (name: string) => `Enjoy ${name}, a standout experience that brings a polished sense of discovery to your day.`,
  (name: string) => `Step into ${name}, selected to add an unhurried and memorable dimension to today's itinerary.`,
  (name: string) => `Make time for ${name}, an engaging highlight woven seamlessly into the day's experiences.`,
  (name: string) => `Experience ${name}, a distinctive moment chosen for its sense of place and occasion.`,
  (name: string) => `Savour ${name}, a well-paced addition that elevates the rhythm of the day.`,
];
const luxuryMandatoryActivityPoint = (name: string, sequence: number) => mandatoryActivityPointTemplates[sequence % mandatoryActivityPointTemplates.length](name);
const generatedMandatoryActivityPointAffixes = mandatoryActivityPointTemplates.map((template) => {
  const marker = '__ACTIVITY_NAME__';
  const [prefix, suffix] = template(marker).split(marker);
  return { prefix, suffix };
});
const isGeneratedMandatoryActivityPoint = (point: string) =>
  point.startsWith(LEGACY_MANDATORY_ACTIVITY_POINT_PREFIX) || generatedMandatoryActivityPointAffixes.some(({ prefix, suffix }) =>
    point.startsWith(prefix) && point.endsWith(suffix) && point.length > prefix.length + suffix.length
  );

export const reconcileMandatoryActivityPoints = (
  itinerary: BuilderItineraryDay[],
  activities: Array<Pick<ActivityConfigInput, 'day' | 'name' | 'inclusionType'>>
): BuilderItineraryDay[] => {
  const mandatoryPointsByDay = activities
    .filter((activity) => activity.inclusionType === 'mandatory' && activity.name.trim())
    .reduce<Record<number, string[]>>((result, activity) => {
      const day = Math.max(1, Number(activity.day || 1));
      const sequence = (result[day] || []).length;
      result[day] = [...(result[day] || []), luxuryMandatoryActivityPoint(activity.name.trim(), sequence)];
      return result;
    }, {});

  return itinerary.map((day, index) => ({
    ...day,
    points: [
      ...(day.points || []).filter((point) => !isGeneratedMandatoryActivityPoint(point)),
      ...Array.from(new Set(mandatoryPointsByDay[index + 1] || [])),
    ],
  }));
};
export const isTransferOnlyActivity = (activity: Pick<BuilderActivityPricingRow, 'ticketPricingAvailable'>) =>
  activity.ticketPricingAvailable === false;

export const getActivityCouplePrice = (activity: Pick<BuilderActivityPricingRow, 'adultPrice'>) =>
  Math.max(0, Number(activity.adultPrice || 0)) * BASE_PACKAGE_PASSENGERS;

export const getIncludedActivityTotal = (activities: Array<Pick<BuilderActivityPricingRow, 'adultPrice' | 'inclusionType' | 'ticketPricingAvailable'>>) =>
  activities
    .filter((activity) => activity.inclusionType === 'mandatory' && !isTransferOnlyActivity(activity))
    .reduce((sum, activity) => sum + getActivityCouplePrice(activity), 0);

const nextTempRowId = (() => {
  let sequence = 0;
  return () => `temp-row-${++sequence}`;
})();

const buildEmptyTransferRow = (cabType: PackageCabType = 'Sedan'): PackageCabPricingRow => ({
  tempId: nextTempRowId(),
  cabType,
  maxOccupancy: getCabMaxOccupancy(cabType),
  price: 0,
  isActive: true,
  isDeleted: false,
});

const getDisplayedTransferRows = (rows: PackageCabPricingRow[]) => rows.filter((row) => !row.isDeleted);

const buildTransferTableRows = (rows: PackageCabPricingRow[]): PackageCabPricingRow[] =>
  PACKAGE_CAB_TYPES.map((cabType) => {
    const existingRow = rows.find((row) => !row.isDeleted && row.cabType === cabType);
    return existingRow || buildEmptyTransferRow(cabType);
  });

const buildEmptyActivityPricingRow = (): BuilderActivityPricingRow => ({
  id: nextTempRowId(),
  imageKey: nextTempRowId(),
  name: '',
  day: 1,
  description: '',
  adultPrice: 0,
  childPrice: 0,
  imageFile: null,
  imagePreview: '',
  inclusionType: 'mandatory',
});

const persistedActivityKey = (activity: any, index: number) => String(
  activity?.id || activity?.slug || activity?.activityRef || activity?.activity_ref || `${activity?.name || 'activity'}-${index}`
);

// The itinerary is the day-wise source of truth. Some saved package responses flatten
// activities with a default day, so recover placement from the parent itinerary day.
export const getEditorActivities = (sourcePackage: Pick<TravelPackage, 'activities' | 'itinerary'> | null | undefined): any[] => {
  const savedActivities = sourcePackage?.activities;
  const flatActivities = Array.isArray(savedActivities) ? savedActivities : [];
  const flatByKey = new Map(flatActivities.map((activity, index) => [persistedActivityKey(activity, index), activity]));
  const savedItinerary = sourcePackage?.itinerary;
  const itinerary = Array.isArray(savedItinerary) ? savedItinerary : [];
  const nestedActivities = itinerary.flatMap((itineraryDay: any, dayIndex) => {
    const day = Math.max(1, Number(itineraryDay?.day || dayIndex + 1));
    const activities = Array.isArray(itineraryDay?.activities) ? itineraryDay.activities : [];
    return activities.map((activity: any, activityIndex: number) => {
      const matchingFlatActivity = flatByKey.get(persistedActivityKey(activity, activityIndex));
      return { ...matchingFlatActivity, ...activity, day };
    });
  });

  if (nestedActivities.length === 0) return flatActivities;

  const nestedKeys = new Set(nestedActivities.map((activity, index) => persistedActivityKey(activity, index)));
  return [
    ...nestedActivities,
    ...flatActivities.filter((activity, index) => !nestedKeys.has(persistedActivityKey(activity, index))),
  ];
};

export const normalizeJourneyDraftHighlights = (value: unknown): string[] => {
  const normalized = Array.isArray(value)
    ? normalizeStringArray(value)
    : typeof value === 'string'
      ? value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

  return Array.from(new Set(normalized));
};

export const normalizeReviewTagArray = (value: unknown): string[] => {
  const flattenValue = (item: unknown): string[] => {
    if (typeof item !== 'string') return [];
    const trimmed = item.trim();
    if (!trimmed) return [];
    const unescaped = trimmed.replace(/\\"/g, '"');

    const quotedTokens = Array.from(unescaped.matchAll(/"([^"]+)"/g))
      .map((match) => match[1].trim())
      .filter(Boolean);
    if (quotedTokens.length > 0) {
      return quotedTokens.flatMap(flattenValue);
    }

    const parsedJson = (() => {
      try {
        return JSON.parse(unescaped);
      } catch {
        return null;
      }
    })();

    if (Array.isArray(parsedJson)) {
      return parsedJson.flatMap(flattenValue);
    }
    if (typeof parsedJson === 'string') {
      return flattenValue(parsedJson);
    }

    return unescaped
      .split(',')
      .map((part) => part.trim())
      .map((part) => part
        .replace(/^[\s"'`{]+/, '')
        .replace(/^\[/, '')
        .replace(/[\s"'`}]+$/, '')
        .replace(/\]$/, '')
        .replace(/\\"/g, '"'))
      .filter(Boolean);
  };

  const normalized = Array.isArray(value)
    ? value.flatMap(flattenValue)
    : flattenValue(value);

  return Array.from(new Set(normalized));
};

const HOTEL_GST_THRESHOLD_PER_ROOM_PER_NIGHT = 7500;
const HOTEL_GST_LOW_RATE = 0.05;
const HOTEL_GST_HIGH_RATE = 0.18;

export const getHotelGstRate = (perRoomPerNight: number): number => (
  Number(perRoomPerNight || 0) > HOTEL_GST_THRESHOLD_PER_ROOM_PER_NIGHT
    ? HOTEL_GST_HIGH_RATE
    : HOTEL_GST_LOW_RATE
);

export const calculateHotelPriceWithGst = (basePrice: number, nights: number): {
  basePrice: number;
  gstAmount: number;
  gstRate: number;
  totalPrice: number;
} => {
  const safeBasePrice = Math.max(0, Number(basePrice || 0));
  const safeNights = Math.max(1, Number(nights || 1));
  const perRoomPerNight = safeBasePrice / safeNights;
  const gstRate = getHotelGstRate(perRoomPerNight);
  const gstAmount = Math.round(safeBasePrice * gstRate);
  return {
    basePrice: safeBasePrice,
    gstAmount,
    gstRate,
    totalPrice: safeBasePrice + gstAmount,
  };
};

const getHotelStoredPrice = (hotel: SelectedHotelRecord['hotel']) => {
  return Math.max(0, Number(hotel.price || hotel.display_price || 0));
};

const ReviewTagField: React.FC<{
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  tone?: ReviewTagTone;
  transform?: (value: string) => string;
}> = ({ label, value, onChange, placeholder, tone = 'default', transform }) => {
  const [draft, setDraft] = useState('');

  const addTag = () => {
    const nextValue = transform ? transform(draft) : draft.trim();
    if (!nextValue) return;
    onChange(Array.from(new Set([...(value || []), nextValue])));
    setDraft('');
  };

  const removeTag = (index: number) => {
    onChange(value.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <label className="guided-package-builder__review-field">
      <span>{label}</span>
      <div className="guided-package-builder__tag-field">
        <div className="guided-package-builder__tag-input-row">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            className="package-editor-input"
            placeholder={placeholder}
          />
          <button type="button" className="package-editor-btn-primary" onClick={addTag}>
            Add
          </button>
        </div>
        <div className="guided-package-builder__review-tags">
          {value.map((item, index) => (
            <span key={`${item}-${index}`} className={`guided-package-builder__review-tag guided-package-builder__review-tag--${tone}`}>
              {item}
              <button
                type="button"
                className="guided-package-builder__review-tag-remove"
                onClick={() => removeTag(index)}
                aria-label={`Delete ${label}`}
                title={`Delete ${label}`}
              >
                x
              </button>
            </span>
          ))}
        </div>
      </div>
    </label>
  );
};

const GuidedPackageBuilder: React.FC = () => {
  const { confirmation: exitConfirmation, markSaved } = usePageExitConfirmation('Your in-progress package will be lost.');
  const location = useLocation();
  const locationState: PackageBuilderLocationState | undefined = isPackageBuilderLocationState(location.state)
    ? location.state
    : undefined;
  const permissions = usePermissions();
  const role = useRole();

  const packageToCopy: TravelPackage | undefined = locationState?.packageToCopy;
  const packageToEdit: TravelPackage | undefined = locationState?.packageToEdit;
  const sourcePackage = packageToEdit || packageToCopy;

  const [editingId, setEditingId] = useState<string | null>(packageToEdit?.id ? String(packageToEdit.id) : null);
  const [step, setStep] = useState(0);
  const [packageName, setPackageName] = useState(sourcePackage?.title || '');
  const [subtitle, setSubtitle] = useState(sourcePackage?.subtitle || '');
  const [description, setDescription] = useState(sourcePackage?.description || '');
  const [durationLabel, setDurationLabel] = useState(
    sourcePackage?.durationLabel || sourcePackage?.duration_label || ''
  );
  const [routeLabel, setRouteLabel] = useState(sourcePackage?.routeLabel || sourcePackage?.route_label || '');
  const [destinationSlugs, setDestinationSlugs] = useState<string[]>(
    normalizeStringArray(
      sourcePackage?.destinationSlugs ||
      sourcePackage?.destination_slugs ||
      (sourcePackage as any)?.relatedDestinations ||
      (sourcePackage as any)?.related_destinations
    ).map(slugify)
  );
  const [primaryDestinationSlug, setPrimaryDestinationSlug] = useState(
    sourcePackage?.primaryDestinationSlug ||
    sourcePackage?.primary_destination_slug ||
    destinationSlugs[0] ||
    ''
  );
  const [bestFor, setBestFor] = useState<string[]>(
    normalizeReviewTagArray(sourcePackage?.bestFor || sourcePackage?.best_for || (sourcePackage as any)?.idealFor)
  );
  const [stayStyle, setStayStyle] = useState(sourcePackage?.stayStyle || sourcePackage?.stay_style || '');
  const [seoTitle, setSeoTitle] = useState(sourcePackage?.seo?.title || '');
  const [seoDescription, setSeoDescription] = useState(sourcePackage?.seo?.description || '');
  const [arrivalAt, setArrivalAt] = useState(sourcePackage?.arrivalAt || sourcePackage?.arrival_at || '');
  const [departureFrom, setDepartureFrom] = useState(sourcePackage?.departureFrom || sourcePackage?.departure_from || '');
  const [arrivalOption, setArrivalOption] = useState<LocationOption | null>(() => {
    const label = sourcePackage?.arrivalAt || sourcePackage?.arrival_at || '';
    return label ? { label, value: label, type: 'airport' } : null;
  });
  const [departureOption, setDepartureOption] = useState<LocationOption | null>(() => {
    const label = sourcePackage?.departureFrom || sourcePackage?.departure_from || '';
    return label ? { label, value: label, type: 'airport' } : null;
  });
  const [places, setPlaces] = useState<BuilderPlace[]>(() => {
    const normalized = normalizePackagePlaces(sourcePackage);
    return normalized.length > 0
      ? normalized.map((place) => ({
        cityName: place.cityName,
        cityCode: place.cityCode || '',
        nights: place.nights,
      }))
      : [{ ...EMPTY_PLACE }];
  });
  const [routeLegs, setRouteLegs] = useState<PackageRouteLeg[]>(() => {
    const existingLegs = sourcePackage?.routeLegs || sourcePackage?.route_legs || [];
    return normalizePackageRouteLegs(existingLegs);
  });
  const routeLegDefinitions = useMemo(
    () => buildRouteLegDefinitions(places, arrivalAt, departureFrom, routeLegs),
    [arrivalAt, departureFrom, places, routeLegs]
  );
  const [roughDraft, setRoughDraft] = useState('');
  const [highlights, setHighlights] = useState<string[]>(
    Array.from(new Set(
      normalizeJourneyDraftHighlights(sourcePackage?.highlights)
    ))
  );
  const [itinerary, setItinerary] = useState<BuilderItineraryDay[]>(() => {
    if (Array.isArray(sourcePackage?.itinerary) && sourcePackage!.itinerary.length > 0) {
      return sourcePackage!.itinerary.map((day) => ({
        day: Number(day.day || 0) || 1,
        title: String(day.title || '').trim(),
        points: normalizeStringArray(day.points),
        description: String((day as any).description || '').trim(),
        image: String((day as any).image || '').trim() || undefined,
      }));
    }
    return [];
  });
  const [faq, setFaq] = useState<BuilderFaq[]>(normalizePackageFaq(sourcePackage?.faq));
  const [searchTags, setSearchTags] = useState<string[]>(
    normalizeReviewTagArray(sourcePackage?.searchTags || sourcePackage?.search_tags)
  );
  const [dayPointDrafts, setDayPointDrafts] = useState<Record<number, string>>({});
  const [transferRows, setTransferRows] = useState<PackageCabPricingRow[]>(() => {
    const normalized = normalizePackageCabPricing(sourcePackage);
    return normalized.length > 0 ? normalized : [buildEmptyTransferRow('Sedan')];
  });
  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
  const [heroImagePreview, setHeroImagePreview] = useState(sourcePackage?.hero_image || '');
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [itineraryImageFiles, setItineraryImageFiles] = useState<Record<number, File>>({});
  const [selectedHotels, setSelectedHotels] = useState<SelectedHotelRecord[]>(() => {
    const packageHotels = sourcePackage?.hotels;
    const hotels = Array.isArray(packageHotels) ? packageHotels : [];
    return hotels.map((hotel: any, index) => ({
      placeIndex: Number(hotel?.placeIndex ?? hotel?.place_index ?? index) || 0,
      hotel,
    }));
  });
  const [hotelModalPlaceIndex, setHotelModalPlaceIndex] = useState<number | null>(null);
  const [activityPricingRows, setActivityPricingRows] = useState<BuilderActivityPricingRow[]>(() => {
    const sourceActivities = getEditorActivities(sourcePackage);
    return sourceActivities.length > 0
      ? sourceActivities.map((activity: any, index: number) => ({
        id: String(activity?.id || activity?.slug || `activity-${index + 1}`),
        imageKey: `activity-${String(activity?.id || activity?.slug || index + 1)}`,
        name: String(activity?.name || '').trim(),
        day: Math.max(1, Number(activity?.day || 1)),
        description: String(activity?.description || ''),
        adultPrice: Math.max(0, Number(activity?.adult_price_paise || activity?.price * 100 || 0) / 100),
        childPrice: Math.max(0, Number(activity?.child_price_paise || activity?.price * 100 || 0) / 100),
        imageFile: null,
        imagePreview: String(activity?.picture_url || ''),
        activityRef: activity?.activityRef || activity?.activity_ref || undefined,
        provider: activity?.provider || undefined,
        pricingMode: activity?.pricingMode || activity?.pricing_mode || activity?.bookingMode || activity?.booking_mode || undefined,
        ticketPricingAvailable: typeof activity?.ticketPricingAvailable === 'boolean' ? activity.ticketPricingAvailable : undefined,
        activityCabPrices: Array.isArray(activity?.activityCabPrices) ? activity.activityCabPrices : Array.isArray(activity?.activity_cab_prices) ? activity.activity_cab_prices : undefined,
        supplierSelection: activity?.supplierSelection && typeof activity.supplierSelection === 'object' ? activity.supplierSelection : undefined,
        inclusionType: activity?.inclusionType === 'optional' ? 'optional' : 'mandatory',
      }))
      : [];
  });
  const [visaPrice, setVisaPrice] = useState<number>(() => {
    const visaDetails = Array.isArray((sourcePackage as any)?.visaDetails)
      ? (sourcePackage as any).visaDetails
      : Array.isArray((sourcePackage as any)?.visa_details)
        ? (sourcePackage as any).visa_details
        : [];
    return visaDetails.reduce((sum: number, item: any) => sum + Math.max(0, Number(item?.price || 0)), 0);
  });
  const [visaDocuments, setVisaDocuments] = useState<string[]>(() => {
    const details = Array.isArray((sourcePackage as any)?.visaDetails) ? (sourcePackage as any).visaDetails : Array.isArray((sourcePackage as any)?.visa_details) ? (sourcePackage as any).visa_details : [];
    return Array.isArray(details[0]?.documents) ? details[0].documents : [];
  });
  const [visaDocumentDraft, setVisaDocumentDraft] = useState('');
  const [insurancePrice, setInsurancePrice] = useState<number>(() => {
    const insuranceDetails = Array.isArray((sourcePackage as any)?.insuranceDetails)
      ? (sourcePackage as any).insuranceDetails
      : Array.isArray((sourcePackage as any)?.insurance_details)
        ? (sourcePackage as any).insurance_details
        : [];
    return insuranceDetails.reduce((sum: number, item: any) => sum + Math.max(0, Number(item?.price || 0)), 0);
  });
  const [insuranceDescription, setInsuranceDescription] = useState(() => String(((sourcePackage as any)?.insuranceDetails || (sourcePackage as any)?.insurance_details || [])[0]?.description || ''));
  const [priceOverride, setPriceOverride] = useState<number | ''>(() => {
    const override = Number(sourcePackage?.priceOverride ?? sourcePackage?.price_override ?? 0);
    return Number.isFinite(override) && override > 0 ? override : '';
  });
  const [isPublished, setIsPublished] = useState(Boolean(sourcePackage?.is_published));
  const [contentReviewed, setContentReviewed] = useState(false);
  const [aiRequestMeta, setAiRequestMeta] = useState<PackageAiRequestMeta>({});
  const [generatedFields, setGeneratedFields] = useState<PackageAiGeneratedField[]>(
    []
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [catalogDayIndex, setCatalogDayIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const defaultHotelStartDate = useMemo(() => getDatePlusDays(new Date().toISOString(), 30), []);
  const getSelectedHotelForPlace = useCallback((placeIndex: number) =>
    selectedHotels.find((entry) => entry.placeIndex === placeIndex)?.hotel, [selectedHotels]);
  const displayedTransferRows = useMemo(() => getDisplayedTransferRows(transferRows), [transferRows]);
  const transferTableRows = useMemo(() => buildTransferTableRows(transferRows), [transferRows]);
  const mandatoryActivityPointInputs = useMemo(
    () => activityPricingRows.map(({ day, inclusionType, name }) => ({ day, inclusionType, name })),
    [activityPricingRows]
  );
  useEffect(() => {
    const unclassifiedCatalogRows = activityPricingRows.filter((activity) => activity.activityRef && (activity.ticketPricingAvailable === undefined || !activity.activityCabPrices));
    if (!unclassifiedCatalogRows.length) return;

    let cancelled = false;
    void estimateActivities({
      activityRefs: unclassifiedCatalogRows.map((activity) => activity.activityRef as string),
      travelDate: defaultHotelStartDate,
      adults: BASE_PACKAGE_PASSENGERS,
      children: 0,
      infants: 0,
    }).then((result) => {
      if (cancelled) return;
      setActivityPricingRows((current) => current.map((activity) => {
        if (!activity.activityRef || (activity.ticketPricingAvailable !== undefined && activity.activityCabPrices)) return activity;
        const quote = result.items[activity.activityRef];
        if (!quote || quote.status !== 'AVAILABLE') return activity;
        const transferOnly = Number(quote.ticketTotal || 0) === 0 && Number(quote.transferTotal || 0) > 0;
        return {
          ...activity,
          pricingMode: quote.mode,
          ticketPricingAvailable: !transferOnly,
          activityCabPrices: quote.cabOptions || [],
          adultPrice: transferOnly ? 0 : Math.max(0, Number(quote.amount ?? activity.adultPrice ?? 0)),
          childPrice: transferOnly ? 0 : activity.childPrice,
        };
      }));
    }).catch(() => {
      // Keep existing saved values when the live estimate cannot be retrieved.
    });

    return () => { cancelled = true; };
  }, [activityPricingRows, defaultHotelStartDate]);
  useEffect(() => {
    setItinerary((current) => {
      const next = reconcileMandatoryActivityPoints(current, mandatoryActivityPointInputs);
      const changed = next.some((day, index) => {
        const previous = current[index];
        return day.points.length !== previous.points.length
          || day.points.some((point, pointIndex) => point !== previous.points[pointIndex]);
      });
      return changed ? next : current;
    });
  }, [mandatoryActivityPointInputs]);
  const hotelPricingRows = useMemo(() => places.map((place, index) => {
    const selectedHotel = selectedHotels.find((entry) => entry.placeIndex === index)?.hotel;
    return {
      id: `hotel-${index}`,
      cityName: place.cityName,
      nights: Math.max(1, Number(place.nights || 1)),
      name: selectedHotel?.name || '',
      price: selectedHotel ? getHotelStoredPrice(selectedHotel) : 0,
    };
  }).filter((item) => item.name), [places, selectedHotels]);
  const hotelPricingTotal = useMemo(
    () => hotelPricingRows.reduce((sum, item) => sum + Math.max(0, Number(item.price || 0)), 0),
    [hotelPricingRows]
  );
  const transferPricingTotal = useMemo(
    () => {
      const enteredPrices = transferTableRows
        .map((row) => Math.max(0, Number(row.price || 0)))
        .filter((price) => price > 0);
      if (enteredPrices.length === 0) return 0;
      return Math.min(...enteredPrices);
    },
    [transferTableRows]
  );
  const activityPricingTotal = useMemo(
    () => getIncludedActivityTotal(activityPricingRows),
    [activityPricingRows]
  );
  const calculatedPackageTotal = useMemo(
    () => hotelPricingTotal
      + transferPricingTotal
      + activityPricingTotal
      + visaPrice * BASE_PACKAGE_PASSENGERS
      + insurancePrice * BASE_PACKAGE_PASSENGERS,
    [activityPricingTotal, hotelPricingTotal, insurancePrice, transferPricingTotal, visaPrice]
  );
  const effectivePackageTotal = useMemo(() => {
    const numericOverride = Number(priceOverride);
    return Number.isFinite(numericOverride) && numericOverride > 0 ? numericOverride : calculatedPackageTotal;
  }, [calculatedPackageTotal, priceOverride]);

  const transferValidationErrors = useMemo(() => {
    const messages: string[] = [];
    const groupedRows = new Map<string, PackageCabPricingRow[]>();

    displayedTransferRows
      .filter((row) => row.isActive)
      .forEach((row) => {
        const cabKey = row.cabType;
        groupedRows.set(cabKey, [...(groupedRows.get(cabKey) || []), row]);
      });

    groupedRows.forEach((rows, cabType) => {
      if (rows.length > 1) messages.push(`${cabType} has more than one active transfer row.`);
    });

    return messages;
  }, [displayedTransferRows]);

  const readinessBlockers = useMemo(() => {
    const blockers: string[] = [];
    if (generatedFields.length > 0 && !contentReviewed) {
      blockers.push('Review AI-generated content before publishing.');
    }
    if (!(Number(effectivePackageTotal || 0) > 0)) {
      blockers.push('Set a valid total price.');
    }
    if (!heroImageFile && !heroImagePreview) {
      blockers.push('Upload a hero image.');
    }
    if (transferValidationErrors.length > 0) {
      blockers.push(...transferValidationErrors);
    }
    if (!(role === 'admin' || permissions.canCreatePackages)) {
      blockers.push('You do not have publish permission.');
    }
    return blockers;
  }, [
    effectivePackageTotal,
    contentReviewed,
    generatedFields.length,
    heroImageFile,
    heroImagePreview,
    permissions.canCreatePackages,
    role,
    transferValidationErrors,
  ]);

  const steps = ['Basics', 'Review & Edit', 'Pricing', 'Featured Hotel', 'Media & Publish'];

  const loadAirportOptions = useCallback(async (inputValue: string): Promise<LocationOption[]> => {
    try {
      return await loadAirportLocations(inputValue);
    } catch {
      return [];
    }
  }, []);

  const loadCityOptions = useCallback(async (inputValue: string): Promise<LocationOption[]> => {
    try {
      return await loadCityLocations(inputValue);
    } catch {
      return [];
    }
  }, []);

  const buildPayload = useCallback(() => {
    const normalizedPlaces = places
      .map((place) => ({
        cityName: place.cityName.trim(),
        cityCode: place.cityCode.trim() || undefined,
        nights: Math.max(1, Number(place.nights || 1)),
      }))
      .filter((place) => place.cityName);
    const normalizedHighlights = highlights.map((item) => item.trim()).filter(Boolean);
    const normalizedItinerary = itinerary
      .map((day, index) => ({
        day: index + 1,
        title: day.title.trim(),
        description: (day.description || day.points.join(' ')).trim(),
        points: day.points.map((point) => point.trim()).filter(Boolean),
        ...(day.image ? { image: day.image } : {}),
      }))
      .filter((day) => day.title || day.description || day.points.length > 0);
    const normalizedFaq = faq
      .map((item) => ({
        question: item.question.trim(),
        answer: item.answer.trim(),
      }))
      .filter((item) => item.question && item.answer);
    const normalizedSearchTags = searchTags.map((item) => item.trim()).filter(Boolean);
    const normalizedTransfers = transferRows.map((row) => ({
      id: row.id,
      tempId: row.tempId,
      cabType: row.cabType,
      maxOccupancy: getCabMaxOccupancy(row.cabType),
      price: Math.max(0, Number(row.price || 0)),
      isActive: Number(row.price || 0) > 0,
      isDeleted: Boolean(row.isDeleted),
    }));
    const normalizedHotels = selectedHotels.map(({ placeIndex, hotel }) => ({
      ...hotel,
      placeIndex,
      city: places[placeIndex]?.cityName || hotel.city_name || '',
      city_code: places[placeIndex]?.cityCode || hotel.city_code || '',
    }));
    const normalizedActivities = activityPricingRows
      .map((row) => ({
        id: row.id,
        activity_image_key: row.imageKey,
        name: row.name.trim(),
        day: Math.max(1, Number(row.day || 1)),
        description: row.activityRef ? summarizeRaynaActivityDescription(row.description) : row.description.trim(),
        price: Math.max(0, Number(row.adultPrice || 0)),
        adult_price_paise: Math.round(Math.max(0, Number(row.adultPrice || 0)) * 100),
        child_price_paise: Math.round(Math.max(0, Number(row.childPrice || 0)) * 100),
        ...(row.imageFile ? {} : row.imagePreview ? { picture_url: row.imagePreview } : {}),
        currency: 'INR',
        inclusionType: row.inclusionType,
        ...(row.pricingMode ? { pricingMode: row.pricingMode } : {}),
        ...(row.ticketPricingAvailable === undefined ? {} : { ticketPricingAvailable: row.ticketPricingAvailable }),
        ...(row.activityCabPrices ? { activityCabPrices: row.activityCabPrices } : {}),
        ...(row.supplierSelection ? { supplierSelection: row.supplierSelection } : {}),
        ...(row.activityRef ? { activityRef: row.activityRef, provider: row.provider || 'RAYNA', source: 'catalog' } : { source: 'manual' }),
      }))
      .filter((row) => row.name);
    const activityConfig = buildActivityConfig(normalizedActivities);
    const itineraryWithActivities = normalizedItinerary.map((day) => ({
      ...day,
      activities: normalizedActivities.filter((activity) => activity.day === day.day),
    }));
    const normalizedVisaDetails = visaPrice > 0
      ? [{ type: 'Visa', price: Math.max(0, Number(visaPrice || 0)), validity: '', documents: visaDocuments, timeRequired: '', currency: 'INR' }]
      : [];
    const normalizedInsuranceDetails = insurancePrice > 0
      ? [{ name: 'Insurance', price: Math.max(0, Number(insurancePrice || 0)), currency: 'INR', description: insuranceDescription.trim() }]
      : [];
    const normalizedDestinationSlugs = destinationSlugs.map(slugify).filter(Boolean);
    const normalizedBestFor = bestFor.map((item) => item.trim()).filter(Boolean);

    return {
      title: packageName.trim(),
      stops: normalizedPlaces.map((place, index) => ({
        destinationSlug: normalizedDestinationSlugs[index] || slugify(place.cityName),
        cityName: place.cityName,
        cityCode: place.cityCode,
        nights: place.nights,
      })),
      routeLegs: routeLegDefinitions.map((leg) => ({
        fromStopIndex: leg.fromStopIndex,
        toStopIndex: leg.toStopIndex,
        ...(leg.fromEndpoint ? { fromEndpoint: leg.fromEndpoint } : {}),
        ...(leg.toEndpoint ? { toEndpoint: leg.toEndpoint } : {}),
        ...(leg.distanceKm === undefined ? {} : { distanceKm: Math.max(0, Number(leg.distanceKm)) }),
        ...(leg.durationMinutes === undefined ? {} : { durationMinutes: Math.max(0, Number(leg.durationMinutes)) }),
        ...(leg.transferNote?.trim() ? { transferNote: leg.transferNote.trim() } : {}),
      })),
      subtitle: subtitle.trim(),
      description: description.trim(),
      bestFor: normalizedBestFor,
      inclusions: PACKAGE_INCLUSIONS,
      exclusions: PACKAGE_EXCLUSIONS,
      arrivalAt: arrivalAt.trim(),
      departureFrom: departureFrom.trim(),
      highlights: normalizedHighlights,
      itinerary: itineraryWithActivities,
      faq: normalizedFaq,
      searchTags: normalizedSearchTags,
      packageCabPricing: normalizedTransfers,
      hotels: normalizedHotels,
      activities: normalizedActivities,
      activityConfig,
      visaDetails: normalizedVisaDetails,
      insuranceDetails: normalizedInsuranceDetails,
      isPublished,
      seo: {},
      websiteSeo: {
        title: seoTitle.trim(),
        description: seoDescription.trim(),
      },
      priceOverride: Number(priceOverride) > 0 ? Number(priceOverride) : null,
    };
  }, [
    activityPricingRows,
    description,
    highlights,
    insuranceDescription,
    insurancePrice,
    isPublished,
    itinerary,
    packageName,
    places,
    searchTags,
    subtitle,
    transferRows,
    selectedHotels,
    routeLegDefinitions,
    visaDocuments,
    visaPrice,
    arrivalAt,
    bestFor,
    departureFrom,
    destinationSlugs,
    faq,
    priceOverride,
    seoDescription,
    seoTitle,
  ]);

  const persistPackage = useCallback(async ({
    publish,
  }: {
    publish?: boolean;
  } = {}) => {
    const payload = buildPayload();
    if (!payload.title) {
      setError('Package name is required.');
      return;
    }
    if (payload.stops.length === 0) {
      setError('Add at least one city and night split.');
      return;
    }
    const incompleteActivities = activityPricingRows.filter((row) => {
      const hasAnyValue = Boolean(
        row.name.trim()
        || row.description.trim()
        || Number(row.adultPrice || 0)
        || Number(row.childPrice || 0)
      );
      return hasAnyValue && (!row.name.trim() || !row.description.trim() || (!row.imageFile && !row.imagePreview));
    });
    if (incompleteActivities.length > 0) {
      const missingImages = incompleteActivities
        .filter((row) => !row.imageFile && !row.imagePreview)
        .map((row) => row.name.trim() || 'unnamed activity');
      const missingDescriptions = incompleteActivities
        .filter((row) => !row.description.trim())
        .map((row) => row.name.trim() || 'unnamed activity');
      setError(
        missingImages.length > 0
          ? `Select an image for: ${missingImages.join(', ')}.`
          : missingDescriptions.length > 0
            ? `Add a description for: ${missingDescriptions.join(', ')}.`
            : 'Complete every activity before saving.'
      );
      return;
    }
    if (publish && readinessBlockers.length > 0) {
      setError(readinessBlockers[0]);
      return;
    }

    setError('');
    setSaving(true);

    const formData = new FormData();
    formData.append('title', payload.title);
    if (payload.subtitle) formData.append('subtitle', payload.subtitle);
    if (payload.description) formData.append('description', payload.description);
    formData.append('stops', JSON.stringify((payload as any).stops || []));
    formData.append('routeLegs', JSON.stringify((payload as any).routeLegs || []));
    formData.append('bestFor', JSON.stringify((payload as any).bestFor || []));
    formData.append('inclusions', JSON.stringify((payload as any).inclusions || []));
    if (payload.arrivalAt) formData.append('arrivalAt', payload.arrivalAt);
    if (payload.departureFrom) formData.append('departureFrom', payload.departureFrom);
    formData.append('highlights', JSON.stringify(payload.highlights));
    formData.append('itinerary', JSON.stringify(payload.itinerary));
    formData.append('faq', JSON.stringify(payload.faq));
    formData.append('searchTags', JSON.stringify(payload.searchTags));
    formData.append('packageCabPricing', JSON.stringify(payload.packageCabPricing));
    formData.append('hotels', JSON.stringify(payload.hotels));
    formData.append('activities', JSON.stringify((payload as any).activities || []));
    formData.append('activityConfig', JSON.stringify((payload as any).activityConfig || { version: 1, days: [] }));
    formData.append('visaDetails', JSON.stringify((payload as any).visaDetails || []));
    formData.append('insuranceDetails', JSON.stringify((payload as any).insuranceDetails || []));
    formData.append('priceOverride', payload.priceOverride === null ? '' : String(payload.priceOverride));
    formData.append('isPublished', String(Boolean(publish ? true : payload.isPublished)));
    if (heroImageFile) formData.append('hero_image', heroImageFile);
    if (galleryFiles.length > 0) galleryFiles.forEach((file) => formData.append('gallery', file));
    Object.entries(itineraryImageFiles).forEach(([dayIndex, file]) => {
      formData.append(`itineraryImages[${dayIndex}]`, file);
    });
    activityPricingRows
      .filter((row) => row.name.trim() && row.imageFile)
      .forEach((row) => formData.append(`activityImages[${row.imageKey}]`, row.imageFile as File));

    try {
      const saved = editingId
        ? await updatePackage(editingId, formData)
        : await createPackage(formData);
      if (saved?.id) setEditingId(String(saved.id));
      if (publish) setIsPublished(true);
      markSaved();
    } catch (saveError: any) {
      const message = saveError?.message || 'Failed to save package';
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [activityPricingRows, buildPayload, editingId, galleryFiles, heroImageFile, itineraryImageFiles, markSaved, readinessBlockers]);

  const handleGenerate = useCallback(async () => {
    if (!packageName.trim()) {
      setError('Package name is required before AI generation.');
      return;
    }
    const validPlaces = places
      .map((place) => ({
        cityName: place.cityName.trim(),
        cityCode: place.cityCode.trim() || undefined,
        nights: Math.max(1, Number(place.nights || 1)),
      }))
      .filter((place) => place.cityName);
    if (validPlaces.length === 0) {
      setError('Add at least one city before AI generation.');
      return;
    }
    setError('');
    setIsGenerating(true);
    try {
      const inferredDestinationSlugs = destinationSlugs.length > 0
        ? destinationSlugs
        : validPlaces.map((place) => slugify(place.cityName)).filter(Boolean);
      const generated = await generateWebsiteV2JourneyDraft({
        title: packageName.trim(),
        destinationSlugs: inferredDestinationSlugs,
        durationLabel: durationLabel.trim() || `${validPlaces.reduce((sum, place) => sum + place.nights, 0) + 1} days`,
        routeLabel: routeLabel.trim() || validPlaces.map((place) => place.cityName).join(' + '),
        styleTags: searchTags,
        roughDraft: roughDraft.trim() || undefined,
      });
      const draft = generated.draft || {};
      if (draft.title) setPackageName(draft.title);
      setSubtitle('');
      setDescription(draft.summary || '');
      setDurationLabel(draft.durationLabel || '');
      setRouteLabel(draft.routeLabel || '');
      setDestinationSlugs(Array.isArray(draft.destinationSlugs) ? draft.destinationSlugs.map(slugify).filter(Boolean) : inferredDestinationSlugs);
      setPrimaryDestinationSlug(draft.primaryDestinationSlug || inferredDestinationSlugs[0] || '');
      setBestFor(normalizeReviewTagArray(draft.bestFor));
      setHighlights(normalizeJourneyDraftHighlights(draft.highlights));
      setItinerary(Array.isArray(draft.itinerary) ? draft.itinerary.map((day, index) => normalizeItineraryDraftDay(day, index)) : []);
      setFaq(normalizePackageFaq(draft.faq));
      setSearchTags(normalizeReviewTagArray(draft.styleTags));
      setStayStyle(draft.stayStyle || '');
      setSeoTitle(draft.seo?.title || '');
      setSeoDescription(draft.seo?.description || '');
      setGeneratedFields((generated.generatedFields || AI_MANAGED_FIELDS) as PackageAiGeneratedField[]);
      setAiRequestMeta({
        requestId: generated.requestId,
        provider: generated.provider,
        model: generated.model,
        generatedFields: generated.generatedFields as PackageAiGeneratedField[] | undefined,
        generatedAt: new Date().toISOString(),
      });
      setContentReviewed(false);
      setStep(1);
    } catch (generationError: any) {
      setError(generationError?.message || 'Failed to generate package content');
    } finally {
      setIsGenerating(false);
    }
  }, [destinationSlugs, durationLabel, packageName, places, routeLabel, roughDraft, searchTags]);

  const updatePlace = (index: number, field: keyof BuilderPlace, value: string | number) => {
    setPlaces((current) => current.map((place, itemIndex) => {
      if (itemIndex !== index) return place;
      if (field === 'nights') {
        return { ...place, nights: Math.max(1, Number(value || 1)) };
      }
      return { ...place, [field]: String(value) };
    }));
  };

  const updateRouteLeg = (index: number, field: 'distanceKm' | 'durationMinutes' | 'transferNote', value: string) => {
    setRouteLegs((current) => {
      const next = buildRouteLegDefinitions(places, arrivalAt, departureFrom, current)
        .map(({ key: _key, fromLabel: _fromLabel, toLabel: _toLabel, ...leg }) => leg);
      if (field === 'transferNote') next[index] = { ...next[index], transferNote: value };
      else {
        const parsed = value === '' ? undefined : Math.max(0, Number(value));
        next[index] = { ...next[index], [field]: parsed !== undefined && Number.isFinite(parsed) ? parsed : undefined };
      }
      return next;
    });
  };

  const updateItineraryDay = (index: number, field: 'title' | 'description', value: string) => {
    setItinerary((current) => current.map((day, itemIndex) => {
      if (itemIndex !== index) return day;
      return { ...day, [field]: value };
    }));
  };

  const addDayPoint = (index: number) => {
    const draft = (dayPointDrafts[index] || '').trim();
    if (!draft) return;
    setItinerary((current) => current.map((day, itemIndex) => {
      if (itemIndex !== index) return day;
      return { ...day, points: Array.from(new Set([...(day.points || []), draft])) };
    }));
    setDayPointDrafts((current) => ({ ...current, [index]: '' }));
  };

  const removeDayPoint = (dayIndex: number, pointIndex: number) => {
    setItinerary((current) => current.map((day, itemIndex) => {
      if (itemIndex !== dayIndex) return day;
      return { ...day, points: day.points.filter((_, index) => index !== pointIndex) };
    }));
  };

  const updateFaq = (index: number, field: keyof BuilderFaq, value: string) => {
    setFaq((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const updateTransferPriceByCabType = (cabType: PackageCabType, value: string) => {
    const nextPrice = Math.max(0, Number(value || 0));
    setTransferRows((current) => {
      const existingRow = current.find((row) => !row.isDeleted && row.cabType === cabType);
      if (existingRow) {
        return current.map((row) => {
          if ((row.id || row.tempId) !== (existingRow.id || existingRow.tempId)) return row;
          return {
            ...row,
            price: nextPrice,
            isActive: nextPrice > 0,
          };
        });
      }
      return [
        ...current,
        {
          ...buildEmptyTransferRow(cabType),
          price: nextPrice,
          isActive: nextPrice > 0,
        },
      ];
    });
  };

  const openActivityPicker = (dayIndex: number) => {
    setCatalogDayIndex(dayIndex);
  };

  const activityPickerContext = useMemo(() => {
    if (catalogDayIndex === null) return null;
    const itineraryDay = catalogDayIndex + 1;
    let accumulatedNights = 0;
    let matchingPlace = places[places.length - 1];
    for (const place of places) {
      accumulatedNights += Math.max(1, Number(place.nights || 1));
      if (itineraryDay <= accumulatedNights) {
        matchingPlace = place;
        break;
      }
    }
    return {
      cityName: matchingPlace?.cityName || undefined,
      travelDate: getDatePlusDays(defaultHotelStartDate, catalogDayIndex),
      travellers: DEFAULT_HOTEL_TRAVELLERS,
    };
  }, [catalogDayIndex, defaultHotelStartDate, places]);

  const addLiveCatalogActivity = (selection: ActivityOptionSelection) => {
    if (catalogDayIndex === null) return;
    setActivityPricingRows((current) => [...current, {
      ...buildEmptyActivityPricingRow(),
      id: `rayna-${selection.tourId}-${selection.tourOptionId}-${selection.transfer.transferId}`,
      imageKey: `rayna-${selection.tourId}-${selection.tourOptionId}-${selection.transfer.transferId}`,
      day: catalogDayIndex + 1,
      name: `${selection.tourName} — ${selection.optionName}`,
      description: selection.description || '',
      imagePreview: selection.imageUrl || '',
      activityRef: selection.activityRef,
      provider: 'RAYNA',
      adultPrice: selection.transfer.adultPrice,
      childPrice: selection.transfer.childPrice,
      ticketPricingAvailable: true,
      inclusionType: 'mandatory',
      supplierSelection: {
        provider: 'RAYNA', activityRef: selection.activityRef, tourId: selection.tourId,
        contractId: selection.contractId, tourOptionId: selection.tourOptionId,
        optionName: selection.optionName, transferId: selection.transfer.transferId,
        transferName: selection.transfer.transferName, travelDate: selection.travelDate,
        travellers: selection.travellers, startTime: selection.transfer.startTime,
        requiresTimeSlot: selection.transfer.isSlot, quotedTotal: selection.transfer.totalPrice,
        currency: selection.transfer.currency, quotedAt: selection.quotedAt || null,
      },
    }]);
    setCatalogDayIndex(null);
  };

  const setPlaceOption = (index: number, option: LocationOption | null) => {
    setPlaces((current) => current.map((place, itemIndex) => (
      itemIndex === index
        ? {
          ...place,
          cityName: option?.label || '',
          cityCode: option?.value || '',
        }
        : place
    )));
  };

  const openHotelModal = (placeIndex: number) => {
    if (!places[placeIndex]?.cityName) {
      setError('Select a city before choosing a hotel.');
      return;
    }
    setError('');
    setHotelModalPlaceIndex(placeIndex);
  };

  const closeHotelModal = () => {
    setHotelModalPlaceIndex(null);
  };

  const handleSelectHotel = (hotel: Hotel) => {
    if (hotelModalPlaceIndex === null) return;
    const offsetDays = places
      .slice(0, hotelModalPlaceIndex)
      .reduce((sum, place) => sum + Math.max(0, Number(place.nights || 0)), 0);
    const placeCheckIn = getDatePlusDays(defaultHotelStartDate, offsetDays);
    const placeCheckOut = getDatePlusDays(placeCheckIn, Math.max(1, Number(places[hotelModalPlaceIndex]?.nights || 1)));
    const selectedNights = Math.max(1, Number(places[hotelModalPlaceIndex]?.nights || 1));
    const hotelPricing = calculateHotelPriceWithGst(Number(hotel.price || hotel.display_price || 0), selectedNights);
    const hotelWithDates: Hotel = {
      ...hotel,
      price: hotelPricing.totalPrice,
      display_price: hotelPricing.totalPrice,
      checkInDate: placeCheckIn,
      checkOutDate: placeCheckOut,
    };
    setSelectedHotels((current) => {
      const next = current.filter((entry) => entry.placeIndex !== hotelModalPlaceIndex);
      next.push({
        placeIndex: hotelModalPlaceIndex,
        hotel: {
          ...hotelWithDates,
        },
      });
      return next.sort((a, b) => a.placeIndex - b.placeIndex);
    });
    setHotelModalPlaceIndex(null);
  };

  const handleNext = async () => {
    if (step === 0) {
      await handleGenerate();
      return;
    }
    setStep((current) => Math.min(steps.length - 1, current + 1));
  };

  return (
    <Layout>
      {exitConfirmation}
      <div className="guided-package-builder">
        <div className="guided-package-builder__header">
          <div>
            <h2>{editingId ? 'Edit Package' : 'New Package'}</h2>
          </div>
        </div>

        <div className="guided-package-builder__steps">
          {steps.map((label, index) => (
            <button
              key={label}
              type="button"
              className={`guided-package-builder__step ${step === index ? 'active' : ''}`}
              onClick={() => setStep(index)}
            >
              <span>{index + 1}</span>
              {label}
            </button>
          ))}
        </div>

        {error && <div className="package-editor-error">{error}</div>}

        {step === 0 && (
          <section className="guided-package-builder__panel">
            <h3>Basics</h3>
            <div className="guided-package-builder__grid">
              <label>
                <span>Package name</span>
                <input value={packageName} onChange={(e) => setPackageName(e.target.value)} className="package-editor-input" />
              </label>
              <label>
                <span>Arrival at</span>
                <AsyncSelect<LocationOption, false>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirportOptions}
                  getOptionLabel={formatAirportOptionLabel}
                  value={arrivalOption}
                  onChange={(option) => {
                    setArrivalOption(option);
                    setArrivalAt(option?.value || '');
                  }}
                  placeholder="Type or select an airport"
                  classNamePrefix="package-editor-select"
                />
              </label>
              <label>
                <span>Departure from</span>
                <AsyncSelect<LocationOption, false>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirportOptions}
                  getOptionLabel={formatAirportOptionLabel}
                  value={departureOption}
                  onChange={(option) => {
                    setDepartureOption(option);
                    setDepartureFrom(option?.value || '');
                  }}
                  placeholder="Type or select an airport"
                  classNamePrefix="package-editor-select"
                />
              </label>
            </div>
            <div className="guided-package-builder__section-head guided-package-builder__section-head--route">
              <div>
                <h4>Cities & Nights</h4>
                <div className="guided-package-builder__section-subtitle">Add the cities in your itinerary</div>
              </div>
              <button type="button" className="package-editor-btn-secondary" onClick={() => { setPlaces((current) => [...current, { ...EMPTY_PLACE }]); setRouteLegs((current) => [...current, { fromStopIndex: places.length - 1, toStopIndex: places.length }]); }}>
                + Add city
              </button>
            </div>
            <div className="guided-package-builder__stack">
              {places.map((place, index) => (
                <div key={`place-${index}`} className="guided-package-builder__row-card">
                  <div className="guided-package-builder__route-step">
                    <div className="guided-package-builder__route-step-badge">{index + 1}</div>
                  </div>
                  <div className="guided-package-builder__route-fields">
                    <label>
                      <span>City</span>
                      <AsyncSelect<LocationOption, false>
                        cacheOptions
                        defaultOptions
                        loadOptions={loadCityOptions}
                        value={place.cityName ? { label: place.cityName, value: place.cityCode || place.cityName, type: 'city' } : null}
                        onChange={(option) => setPlaceOption(index, option)}
                        placeholder="Type or select a city"
                        classNamePrefix="package-editor-select"
                      />
                    </label>
                    <label>
                      <span>Nights</span>
                      <input
                        type="number"
                        min={1}
                        value={place.nights}
                        onChange={(e) => updatePlace(index, 'nights', e.target.value)}
                        className="package-editor-input"
                        placeholder="Nights"
                      />
                    </label>
                    <label>
                      <span>Hotel</span>
                      {(() => {
                        const selectedHotel = getSelectedHotelForPlace(index);
                        return selectedHotel ? (
                          <div className="guided-package-builder__hotel-card">
                            <div className="guided-package-builder__hotel-card-body">
                              <strong>{selectedHotel.name}</strong>
                              <div className="guided-package-builder__hotel-summary">
                                {(selectedHotel as any).city || (selectedHotel as any).city_name || place.cityName || 'Selected hotel'}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="package-editor-btn-secondary"
                              onClick={() => openHotelModal(index)}
                            >
                              Change hotel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="package-editor-btn-secondary guided-package-builder__hotel-select"
                            onClick={() => openHotelModal(index)}
                          >
                            Select hotel
                          </button>
                        );
                      })()}
                    </label>
                  </div>
                  <button
                    type="button"
                    className="guided-package-builder__icon-btn"
                    disabled={places.length === 1}
                    onClick={() => { setPlaces((current) => current.filter((_, itemIndex) => itemIndex !== index)); setRouteLegs([]); }}
                    aria-label="Delete city"
                    title="Delete city"
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
            {routeLegDefinitions.length > 0 && <div className="guided-package-builder__stack" style={{ marginTop: 16 }}>
              <div className="guided-package-builder__section-head">
                <div><h4>Route transfers</h4><div className="guided-package-builder__section-subtitle">Optional distance and estimated travel time for airport and city transfers</div></div>
              </div>
              {routeLegDefinitions.map((leg, index) => {
                return <div className="guided-package-builder__route-transfer-card" key={`route-leg-${leg.key}`}>
                  <div className="guided-package-builder__route-transfer-heading">
                    <span>{leg.fromLabel}</span><span className="guided-package-builder__route-transfer-arrow" aria-hidden="true">→</span><span>{leg.toLabel}</span>
                  </div>
                  <div className="guided-package-builder__route-transfer-fields">
                    <label><span>Distance</span><div className="guided-package-builder__input-with-suffix"><input className="package-editor-input" min={0} onChange={(event) => updateRouteLeg(index, 'distanceKm', event.target.value)} placeholder="e.g. 55" type="number" value={leg.distanceKm ?? ''} /><em>km</em></div></label>
                    <label><span>Estimated time</span><div className="guided-package-builder__input-with-suffix"><input className="package-editor-input" min={0} onChange={(event) => updateRouteLeg(index, 'durationMinutes', event.target.value)} placeholder="e.g. 120" type="number" value={leg.durationMinutes ?? ''} /><em>min</em></div></label>
                    <label className="guided-package-builder__route-note">
                      <span>Transfer note <small>optional · up to {TRANSFER_NOTE_MAX_WORDS} words</small></span>
                      <textarea
                        className="package-editor-textarea"
                        onChange={(event) => updateRouteLeg(index, 'transferNote', limitToWordCount(event.target.value))}
                        placeholder="Private scenic transfer"
                        rows={3}
                        value={leg.transferNote ?? ''}
                      />
                      <small>{countWords(leg.transferNote)} / {TRANSFER_NOTE_MAX_WORDS} words</small>
                    </label>
                  </div>
                </div>;
              })}
            </div>}
            <label className="guided-package-builder__review-field guided-package-builder__rough-draft-field">
              <span>Rough draft for AI</span>
              <div className="guided-package-builder__rough-draft-wrap">
                <textarea
                  value={roughDraft}
                  onChange={(e) => setRoughDraft(e.target.value)}
                  className="package-editor-textarea guided-package-builder__textarea-sm"
                  placeholder="Optional notes for the AI draft, such as must-see spots, pacing, or a specific style."
                />
                <div className="guided-package-builder__field-hint">
                  Optional. Sent to the AI draft request as guidance.
                </div>
              </div>
            </label>
          </section>
        )}

        {step === 1 && (
          <section className="guided-package-builder__panel">
            <div className="guided-package-builder__review-header-card">
              <div className="guided-package-builder__review-header-title">
                <h3>Review & Edit Content</h3>
                {aiRequestMeta?.requestId && (
                  <div className="guided-package-builder__meta">
                    Last request: {aiRequestMeta.requestId}
                    {aiRequestMeta.model ? ` • ${aiRequestMeta.model}` : ''}
                    {aiRequestMeta.provider ? ` • ${aiRequestMeta.provider}` : ''}
                  </div>
                )}
              </div>
              <div className="guided-package-builder__header-actions">
                <button type="button" className="package-editor-btn-ghost guided-package-builder__review-regenerate" disabled={isGenerating} onClick={() => void handleGenerate()}>
                  {isGenerating ? 'Generating…' : 'Regenerate AI Content'}
                </button>
                <label className="guided-package-builder__checkbox guided-package-builder__review-checkbox">
                  <input type="checkbox" checked={contentReviewed} onChange={(e) => setContentReviewed(e.target.checked)} />
                  <span>Content reviewed</span>
                </label>
              </div>
            </div>
            <div className="guided-package-builder__review-card">
              <div className="guided-package-builder__review-grid guided-package-builder__review-grid--top">
                <label className="guided-package-builder__review-field">
                  <span>Website duration label</span>
                  <input
                    value={durationLabel}
                    onChange={(e) => setDurationLabel(e.target.value)}
                    className="package-editor-input"
                    placeholder="e.g. 7 days / 6 nights"
                  />
                </label>
                <label className="guided-package-builder__review-field">
                  <span>Website route label</span>
                  <input
                    value={routeLabel}
                    onChange={(e) => setRouteLabel(e.target.value)}
                    className="package-editor-input"
                    placeholder="e.g. Srinagar + Gulmarg + Pahalgam"
                  />
                </label>
                <ReviewTagField
                  label="Destination slugs"
                  value={destinationSlugs}
                  onChange={(next) => {
                    const normalized = next.map(slugify).filter(Boolean);
                    setDestinationSlugs(normalized);
                    setPrimaryDestinationSlug((current) => current || normalized[0] || '');
                  }}
                  placeholder="Add destination slug and press Enter"
                  tone="blue"
                  transform={slugify}
                />
                <label className="guided-package-builder__review-field">
                  <span>Primary destination slug</span>
                  <input
                    value={primaryDestinationSlug}
                    onChange={(e) => setPrimaryDestinationSlug(slugify(e.target.value))}
                    className="package-editor-input"
                    placeholder="e.g. kashmir"
                  />
                </label>
              </div>
              <label className="guided-package-builder__review-field">
                <span>Description</span>
                <div className="guided-package-builder__review-description">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="package-editor-textarea guided-package-builder__textarea-lg"
                    maxLength={2000}
                  />
                  <div className="guided-package-builder__review-char-count">{description.length} / 2000</div>
                </div>
              </label>
              <div className="guided-package-builder__review-grid guided-package-builder__review-grid--middle">
                <ReviewTagField
                  label="Highlights"
                  value={highlights}
                  onChange={setHighlights}
                  placeholder="Add highlight and press Enter"
                  tone="gold"
                />
                <ReviewTagField
                  label="Search tags"
                  value={searchTags}
                  onChange={setSearchTags}
                  placeholder="Add search tag and press Enter"
                  tone="blue"
                />
                <ReviewTagField
                  label="Best for"
                  value={bestFor}
                  onChange={setBestFor}
                  placeholder="Add audience and press Enter"
                  tone="green"
                />
                <label className="guided-package-builder__review-field">
                  <span>Stay style</span>
                  <input
                    value={stayStyle}
                    onChange={(e) => setStayStyle(e.target.value)}
                  className="package-editor-input"
                  placeholder="e.g. Boutique beachfront resorts"
                />
              </label>
              </div>
            </div>
            <div className="guided-package-builder__section-head">
              <h4>Itinerary</h4>
              <button
                type="button"
                className="package-editor-btn-ghost"
                onClick={() => setItinerary((current) => [...current, { day: current.length + 1, title: '', points: [] }])}
              >
                + Add day
              </button>
            </div>
            <div className="guided-package-builder__stack">
              {itinerary.map((day, index) => (
                <div key={`itinerary-${index}`} className="guided-package-builder__itinerary-card">
                  <div className="guided-package-builder__itinerary-title">Day {index + 1}</div>
                  <input
                    value={day.title}
                    onChange={(e) => updateItineraryDay(index, 'title', e.target.value)}
                    className="package-editor-input"
                    placeholder="Day title"
                  />
                  <div className="guided-package-builder__points-block">
                    <div className="guided-package-builder__section-head">
                      <div className="guided-package-builder__points-label">Activities for this day</div>
                      <button type="button" className="package-editor-btn-ghost" onClick={() => void openActivityPicker(index)}>+ Pick Activity</button>
                    </div>
                    {activityPricingRows.filter((activity) => Number(activity.day) === index + 1).map((activity) => (
                      <article className="guided-package-builder__activity-card guided-package-builder__activity-card--summary" key={activity.id}>
                        <div className="guided-package-builder__activity-media">
                          {activity.imagePreview ? <img src={activity.imagePreview} alt={activity.name} /> : <div className="guided-package-builder__activity-media-placeholder">Experience<br />image</div>}
                          {activity.activityRef && <span className="guided-package-builder__activity-source">Rayna catalog</span>}
                        </div>
                        <div className="guided-package-builder__activity-details">
                          <div className="guided-package-builder__activity-summary-heading">
                            <div>
                              <label className="guided-package-builder__activity-inclusion-select">
                                <span>Experience type</span>
                                <div className="guided-package-builder__activity-inclusion-control">
                                  <select
                                    aria-label={`Experience type for ${activity.name || 'activity'}`}
                                    value={activity.inclusionType}
                                    onChange={(event) => setActivityPricingRows((current) => current.map((item) => (
                                      item.id === activity.id ? { ...item, inclusionType: event.target.value as 'mandatory' | 'optional' } : item
                                    )))}
                                  >
                                    <option value="mandatory">Included experience</option>
                                    <option value="optional">Optional add-on</option>
                                  </select>
                                  <span aria-hidden="true">⌄</span>
                                </div>
                                <small>Change inclusion</small>
                              </label>
                              <h5>{activity.name}</h5>
                            </div>
                            <button
                              type="button"
                              className="guided-package-builder__activity-remove"
                              onClick={() => setActivityPricingRows((current) => current.filter((item) => item.id !== activity.id))}
                            >
                              Remove activity
                            </button>
                          </div>
                          {activity.activityRef && <div className="guided-package-builder__activity-reference">Rayna reference: {activity.activityRef}</div>}
                          {activity.description && <p className="guided-package-builder__activity-summary-description">{activity.description}</p>}
                        </div>
                        <aside className="guided-package-builder__activity-side">
                          <div className="guided-package-builder__activity-prices">
                            <div className="guided-package-builder__activity-prices-title">Pricing</div>
                            {isTransferOnlyActivity(activity) ? (
                              <div className="guided-package-builder__activity-transfer-only">Transfer only · priced by cab type</div>
                            ) : <>
                              <div className="guided-package-builder__activity-price-line"><span>Adult</span><strong>INR {Math.max(0, Number(activity.adultPrice || 0)).toLocaleString('en-IN')}</strong><small>per person</small></div>
                              <div className="guided-package-builder__activity-price-line"><span>Child</span><strong>INR {Math.max(0, Number(activity.childPrice || 0)).toLocaleString('en-IN')}</strong><small>per person</small></div>
                              <div className="guided-package-builder__activity-couple-price">INR {getActivityCouplePrice(activity).toLocaleString('en-IN')} / 2 adults</div>
                            </>}
                            <div className="guided-package-builder__activity-cab-prices">
                              <div className="guided-package-builder__activity-cab-prices-title">Cab prices</div>
                              {(activity.activityCabPrices || transferTableRows.map((cab) => ({ cabType: cab.cabType, name: cab.cabType, transferPrice: cab.price }))).map((cab) => (
                                <div className="guided-package-builder__activity-cab-price" key={cab.cabType}>
                                  <span>{cab.name}</span>
                                  <strong>INR {Math.max(0, Number(cab.transferPrice || 0)).toLocaleString('en-IN')}</strong>
                                </div>
                              ))}
                            </div>
                          </div>
                        </aside>
                      </article>
                    ))}
                    {!activityPricingRows.some((activity) => Number(activity.day) === index + 1) && <div className="guided-package-builder__points-empty">No activities added for this day.</div>}
                  </div>
                  <div className="guided-package-builder__itinerary-image-upload">
                    <label>
                      <span>Day image</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setItineraryImageFiles((current) => ({ ...current, [index]: file }));
                          setItinerary((current) => current.map((item, itemIndex) => (
                            itemIndex === index ? { ...item, image: URL.createObjectURL(file) } : item
                          )));
                        }}
                        className="package-editor-input"
                      />
                    </label>
                    {day.image && (
                      <div className="guided-package-builder__itinerary-image-preview">
                        <img src={day.image} alt={`Day ${index + 1} itinerary preview`} />
                        <button
                          type="button"
                          className="package-editor-btn-ghost"
                          onClick={() => {
                            setItineraryImageFiles((current) => {
                              const next = { ...current };
                              delete next[index];
                              return next;
                            });
                            setItinerary((current) => current.map((item, itemIndex) => (
                              itemIndex === index ? { ...item, image: undefined } : item
                            )));
                          }}
                        >
                          Remove image
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="guided-package-builder__points-block">
                    <div className="guided-package-builder__points-label">Day points</div>
                    <div className="package-editor-tag-input-container">
                      <input
                        value={dayPointDrafts[index] || ''}
                        onChange={(e) => setDayPointDrafts((current) => ({ ...current, [index]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addDayPoint(index);
                          }
                        }}
                        className="package-editor-input"
                        placeholder="Add point and press Enter"
                      />
                      <button type="button" className="package-editor-btn-secondary" onClick={() => addDayPoint(index)}>
                        Add
                      </button>
                    </div>
                    {(day.points || []).length > 0 ? (
                      <ol className="guided-package-builder__points-list">
                        {(day.points || []).map((point, pointIndex) => (
                          <li key={`${point}-${pointIndex}`} className="guided-package-builder__point-item">
                            <span className="guided-package-builder__point-text">{point}</span>
                            <button
                              type="button"
                              className="guided-package-builder__point-remove"
                              onClick={() => removeDayPoint(index, pointIndex)}
                              aria-label="Delete point"
                              title="Delete point"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <div className="guided-package-builder__points-empty">No points added yet.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {catalogDayIndex !== null && activityPickerContext && (
              <ActivityTourOptionPicker
                defaultCityName={activityPickerContext.cityName}
                defaultTravelDate={activityPickerContext.travelDate}
                defaultTravellers={activityPickerContext.travellers}
                onClose={() => setCatalogDayIndex(null)}
                onSelect={addLiveCatalogActivity}
              />
            )}
            <div className="guided-package-builder__section-head">
              <h4>FAQ</h4>
              <button
                type="button"
                className="package-editor-btn-ghost"
                onClick={() => setFaq((current) => [...current, { question: '', answer: '' }])}
              >
                + Add FAQ
              </button>
            </div>
            <div className="guided-package-builder__stack">
              {faq.map((item, index) => (
                <div key={`faq-${index}`} className="guided-package-builder__faq-card">
                  <input
                    value={item.question}
                    onChange={(e) => updateFaq(index, 'question', e.target.value)}
                    className="package-editor-input"
                    placeholder="Question"
                  />
                  <textarea
                    value={item.answer}
                    onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                    className="package-editor-textarea"
                    placeholder="Answer"
                  />
                </div>
              ))}
            </div>
            <div className="guided-package-builder__section-head">
              <h4>Website SEO</h4>
            </div>
            <div className="guided-package-builder__grid">
              <label>
                <span>SEO title</span>
                <input
                  value={seoTitle}
                  onChange={(e) => setSeoTitle(e.target.value)}
                  className="package-editor-input"
                />
              </label>
              <label>
                <span>SEO description</span>
                <input
                  value={seoDescription}
                  onChange={(e) => setSeoDescription(e.target.value)}
                  className="package-editor-input"
                />
              </label>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="guided-package-builder__panel">
            <h3>Pricing</h3>
            <div className="guided-package-builder__stack">
                <div className="guided-package-builder__pricing-section">
                  <div className="guided-package-builder__section-head">
                    <h4>Hotels</h4>
                  </div>
                  <div className="guided-package-builder__help">
                    Hotel GST is included at 5% up to INR 7,500 per room per night and 18% above that.
                  </div>
                  <div className="guided-package-builder__stack">
                    {hotelPricingRows.length === 0 ? (
                      <div className="guided-package-builder__empty-state">Select hotels in Basics to price them here.</div>
                    ) : hotelPricingRows.map((hotel) => (
                      <div key={hotel.id} className="guided-package-builder__pricing-row">
                      <div>
                        <strong>{hotel.name}</strong>
                        <div className="guided-package-builder__help">{hotel.nights} night{hotel.nights > 1 ? 's' : ''}</div>
                      </div>
                      <div className="guided-package-builder__pricing-row-price">INR {hotel.price.toLocaleString('en-IN')}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="guided-package-builder__pricing-section">
                <div className="guided-package-builder__section-head"><h4>Activities</h4><strong>INR {activityPricingTotal.toLocaleString('en-IN')} / 2 adults</strong></div>
                <div className="guided-package-builder__stack">
                  {activityPricingRows.filter((activity) => activity.name.trim()).length === 0 ? <div className="guided-package-builder__empty-state">Add activities from the day-wise itinerary to see their pricing here.</div> : activityPricingRows.filter((activity) => activity.name.trim()).map((activity) => (
                    <div key={activity.id} className="guided-package-builder__pricing-row" style={{ alignItems: 'center' }}>
                      {activity.imagePreview && <img src={activity.imagePreview} alt="" style={{ width: 52, height: 42, objectFit: 'cover', borderRadius: 6 }} />}
                      <div><strong>{activity.name}</strong><div className="guided-package-builder__help">Day {activity.day} · {activity.inclusionType === 'mandatory' ? 'Mandatory / included' : 'Optional add-on'}{activity.activityRef ? ` · ${activity.activityRef}` : ''}</div><div className="guided-package-builder__help">{cleanActivityDescription(activity.description).slice(0, 120)}</div></div>
                      <div className="guided-package-builder__pricing-row-price">INR {getActivityCouplePrice(activity).toLocaleString('en-IN')} / 2 adults</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="guided-package-builder__pricing-section">
                <div className="guided-package-builder__section-head">
                  <h4>Transfers</h4>
                </div>
                <div className="guided-package-builder__table-wrap">
                  <table className="guided-package-builder__table">
                    <thead>
                      <tr>
                        <th>Cab type</th>
                        <th>Max occupancy</th>
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferTableRows.map((row) => (
                        <tr key={row.cabType}>
                          <td>{row.cabType}</td>
                          <td>{row.maxOccupancy}</td>
                          <td>
                            <input
                              type="number"
                              min={0}
                              value={row.price}
                              onChange={(e) => updateTransferPriceByCabType(row.cabType, e.target.value)}
                              className="package-editor-input"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="guided-package-builder__help">
                  The lowest entered cab price is included in total pricing.
                </div>
              </div>
              <div className="guided-package-builder__pricing-section">
                <div className="guided-package-builder__section-head">
                  <h4>Other Components</h4>
                </div>
                <div className="guided-package-builder__grid" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="guided-package-builder__other-component-row">
                    <strong>Visa</strong>
                  <label>
                    <span>Visa price (per person)</span>
                    <input
                      type="number"
                      min={0}
                      value={visaPrice}
                      onChange={(e) => setVisaPrice(Math.max(0, Number(e.target.value || 0)))}
                      className="package-editor-input"
                    />
                  </label>
                  <label>
                    <span>Visa documents</span>
                    <div className="guided-package-builder__tag-input-row"><input value={visaDocumentDraft} onChange={(e) => setVisaDocumentDraft(e.target.value)} className="package-editor-input" placeholder="e.g. Passport copy" /><button type="button" className="package-editor-btn-primary" onClick={() => { const item = visaDocumentDraft.trim(); if (item) { setVisaDocuments((current) => [...current, item]); setVisaDocumentDraft(''); } }}>Add</button></div>
                    {visaDocuments.map((document, index) => <span key={`${document}-${index}`} className="guided-package-builder__review-tag">{document}<button type="button" onClick={() => setVisaDocuments((current) => current.filter((_, itemIndex) => itemIndex !== index))}>×</button></span>)}
                  </label>
                  </div>
                  <div className="guided-package-builder__other-component-row">
                    <strong>Insurance</strong>
                  <label>
                    <span>Insurance price (per person)</span>
                    <input
                      type="number"
                      min={0}
                      value={insurancePrice}
                      onChange={(e) => setInsurancePrice(Math.max(0, Number(e.target.value || 0)))}
                      className="package-editor-input"
                    />
                  </label>
                  <label><span>Insurance description <small>(optional)</small></span><input value={insuranceDescription} onChange={(e) => setInsuranceDescription(e.target.value)} className="package-editor-input" placeholder="Coverage details" /></label>
                  </div>
                </div>
              </div>
              <div className="guided-package-builder__price-summary">
                <span>Calculated total pricing</span>
                <strong>INR {calculatedPackageTotal.toLocaleString('en-IN')}</strong>
                <small>Hotels + transfers + activities + visa + insurance</small>
                <label>
                  <span>Override total price</span>
                  <input
                    type="number"
                    min={0}
                    value={priceOverride}
                    onChange={(e) => setPriceOverride(e.target.value === '' ? '' : Math.max(0, Number(e.target.value || 0)))}
                    className="package-editor-input"
                    placeholder="Optional override"
                  />
                </label>
                <div className="guided-package-builder__pricing-total">
                  Final total: <strong>INR {effectivePackageTotal.toLocaleString('en-IN')}</strong>
                </div>
                <div className="guided-package-builder__help">
                  Legacy starting price fields will be derived from this package pricing on save.
                </div>
              </div>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="guided-package-builder__panel">
            <h3>Featured Hotel</h3>
            <FeaturedHotelsSection places={places} checkIn={defaultHotelStartDate} />
          </section>
        )}

        {step === 4 && (
          <section className="guided-package-builder__panel">
            <h3>Media & Publish</h3>
            <div className="guided-package-builder__grid">
              <label>
                <span>Hero image</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setHeroImageFile(file);
                    if (file) setHeroImagePreview(URL.createObjectURL(file));
                  }}
                  className="package-editor-input"
                />
              </label>
              <label>
                <span>Gallery</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
                  className="package-editor-input"
                />
              </label>
            </div>
            {heroImagePreview && (
              <div className="guided-package-builder__hero-preview">
                <img src={heroImagePreview} alt="Hero preview" />
              </div>
            )}
            <div className="guided-package-builder__checklist">
              <h4>Publish readiness</h4>
              <ul>
                {readinessBlockers.length === 0 ? (
                  <li>Ready to publish</li>
                ) : (
                  readinessBlockers.map((blocker) => <li key={blocker}>{blocker}</li>)
                )}
              </ul>
            </div>
            <label className="guided-package-builder__checkbox">
              <input type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
              <span>Mark package as published on next save</span>
            </label>
          </section>
        )}

        <div className="guided-package-builder__footer">
          {step > 0 ? (
            <button type="button" className="package-editor-btn-ghost" onClick={() => setStep((current) => Math.max(0, current - 1))}>
              Back
            </button>
          ) : (
            <div aria-hidden="true" />
          )}
          {step === steps.length - 1 ? (
            <button
              type="button"
              className="package-editor-btn-primary"
              disabled={saving}
              onClick={() => void persistPackage({ publish: isPublished })}
            >
              {saving ? 'Saving…' : isPublished ? 'Save & Publish' : 'Save Package'}
            </button>
          ) : (
            <button type="button" className="package-editor-btn-primary" disabled={isGenerating} onClick={() => void handleNext()}>
              {step === 0 ? (isGenerating ? 'Generating…' : 'Next') : 'Next'}
            </button>
          )}
        </div>
        {hotelModalPlaceIndex !== null && (() => {
          const place = places[hotelModalPlaceIndex];
          const offsetDays = places
            .slice(0, hotelModalPlaceIndex)
            .reduce((sum, item) => sum + Math.max(0, Number(item.nights || 0)), 0);
          const checkIn = getDatePlusDays(defaultHotelStartDate, offsetDays);
          const checkOut = getDatePlusDays(checkIn, Math.max(1, Number(place?.nights || 1)));
          return (
            <HotelListModal
              place={{
                label: place.cityName,
                value: place.cityCode || place.cityName,
                nights: Math.max(1, Number(place.nights || 1)),
              }}
              checkIn={checkIn}
              checkOut={checkOut}
              travellers={DEFAULT_HOTEL_TRAVELLERS}
              onClose={closeHotelModal}
              onSelect={handleSelectHotel}
            />
          );
        })()}
      </div>
    </Layout>
  );
};

export default GuidedPackageBuilder;
